import { getCollectionThreadsQueryOptions } from '#pplx/frontend/apps/ask/hooks/collections-hooks/collectionThreadsQueryOptions.js';
import { getProjectThreadsQueryKeyPrefixes } from '#pplx/frontend/apps/ask/queries/query-keys/collectionKeys.js';
import { LIBRARY_ITEM_LIMIT } from '#pplx/frontend/apps/ask/queries/thread-queries/constants.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useInfiniteQuery } from '#pplx/frontend/react-query/useInfiniteQuery.js';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
export const useCollectionThreadsQuery = ({ collectionSlug, autoFetch, filterByUser = false, filterBySharedThreads = false, includeUserAndSharedThreads = false, threadType, includeAssets = false, includeArticles = false, archivedOnly = false, limit = LIBRARY_ITEM_LIMIT, searchTerm, staleTime, refetchOnMount, reason, throwOnError = false, }) => {
    if (!collectionSlug) {
        logger.log('collectionSlug is required for useCollectionThreadsQuery');
    }
    const queryClient = useQueryClient();
    const query = useInfiniteQuery({
        ...getCollectionThreadsQueryOptions({
            collectionSlug,
            filterByUser,
            filterBySharedThreads,
            includeUserAndSharedThreads,
            threadType,
            includeAssets,
            includeArticles,
            archivedOnly,
            limit,
            searchTerm,
            reason,
            throwOnError,
        }),
        enabled: autoFetch,
        placeholderData: keepPreviousData,
        staleTime,
        refetchOnMount,
    });
    const pages = (query.data?.pages ?? EMPTY_ARRAY);
    const threads = useMemo(() => pages.flat(), [pages]);
    const hasNextPage = pages[pages.length - 1]?.[0]?.has_next_page;
    const page = query.data?.pageParams?.length ?? 1;
    const getNextPage = useCallback(() => query.fetchNextPage(), [query]);
    const getPreviousPage = useCallback(() => query.fetchPreviousPage(), [query]);
    const paginationInfo = useMemo(() => ({
        currentPage: page,
        hasNextPage: !!hasNextPage,
        hasPreviousPage: page > 1,
        getNextPage,
        getPreviousPage,
    }), [page, hasNextPage, getNextPage, getPreviousPage]);
    const invalidate = useCallback((slug) => {
        getProjectThreadsQueryKeyPrefixes(slug ?? '').forEach((queryKey) => {
            void queryClient.refetchQueries({ queryKey });
        });
    }, [queryClient]);
    return useMemo(() => ({
        query,
        invalidate,
        paginationInfo,
        threads,
    }), [invalidate, paginationInfo, query, threads]);
};
//# sourceMappingURL=useCollectionThreadsQuery.js.map